<script lang="ts" setup>
	import useAccountApi from '~/services/apis/account'
	import { useResourcesApi } from '~/services/apis/resources'
	import { TimeOut } from '~/services/apis/timeout'

	const { Gender, User, StatesWithLga } = reactor(useUser())

	const { getStatesWithLGA } = useResourcesApi()
	const { UpdateUserBio, GetAccountInfo } = useAccountApi()
	const accountDetails = computed(() => ({
		first_name: User.value?.first_name || '',
		last_name: User.value?.last_name || '',
		email: User.value?.email || '',
		phone: User.value?.phone || '',
		gender: User.value?.gender || '',
		city: User.value?.city || '',
		address: User.value?.address || '',
		state: User.value?.state || '',
		country: 'nigeria'
	}))
	const formData = ref<UserUpdatePayload>({
		first_name: '',
		last_name: '',
		email: '',
		phone: '',
		gender: '',
		city: '',
		address: '',
		state: User.value?.state || '',
		country: 'nigeria'
	})
	const loading = shallowRef(false)
	const emits = defineEmits<{
		(event: 'done', param: UserUpdatePayload | {}): void
	}>()

	const city = computed(() => {
		return StatesWithLga.value.find(item => item.value === formData.value?.state)?.lga || []
	})

	const updateFormData = () => {
		formData.value = {
			first_name: accountDetails.value?.first_name,
			last_name: accountDetails.value?.last_name,
			email: accountDetails.value?.email,
			phone: accountDetails.value?.phone,
			gender: accountDetails.value?.gender,
			city: accountDetails.value?.city,
			address: accountDetails.value?.address,
			state: accountDetails.value?.state,
			country: 'nigeria'
		}
	}

	const isFormSame = computed(() => {
		return JSON.stringify(accountDetails.value) === JSON.stringify(formData.value)
	})

	const submitForm = async () => {
		try {
			loading.value = true
			const result = await UpdateUserBio(User.value?.id || '', formData.value)
			if (result?.email) {
				useNotification().alert({
					status: 200,
					message: 'Profile Updated successfully'
				})
				TimeOut(2000).then(async () => await GetAccountInfo(null, 0))
				updateFormData()
			}
		} finally {
			loading.value = false
		}
		emits('done', formData.value)
	}
	onMounted(() => {
		if (!StatesWithLga.value.length) {
			getStatesWithLGA()
		}
		updateFormData()
	})
	// Watch For changes and update
	watch(accountDetails, updateFormData, {
		deep: true
	})
</script>

<template>
	<div>
		<div class="space-y-3">
			<a-form :model="formData" :validate-messages="antValidateMessages" @finish="submitForm">
				<a-form-item class="!mb-5" label="First name" required name="first_name">
					<a-input
						v-model:value="formData.first_name"
						class="custom-input !bg-gray-50 !px-5"
						placeholder="John Doe"
					/>
				</a-form-item>

				<a-form-item class="!mb-5" label="Last name" required name="last_name">
					<a-input v-model:value="formData.last_name" class="custom-input !bg-gray-50 !px-5" />
				</a-form-item>

				<a-form-item label="Email Address" required :rules="[{ type: 'email' }]" name="email" class="!mb-5">
					<a-input v-model:value="formData.email" class="custom-input !bg-gray-50 !px-5" />
				</a-form-item>

				<a-form-item label="Phone Number" required name="phone" class="!mb-5">
					<a-input v-model:value="formData.phone" type="tel" class="custom-input !bg-gray-50 !px-5" />
				</a-form-item>

				<a-form-item class="!mb-5" required name="gender" label="Gender">
					<a-select v-model:value="formData.gender" :options="Gender" />
				</a-form-item>

				<a-form-item class="!mb-5" name="state" label="State" required>
					<a-select v-model:value="formData.state" :options="StatesWithLga" show-search />
				</a-form-item>

				<a-form-item class="!mb-5" name="city" label="City" required>
					<a-select v-model:value="formData.city" :options="city" show-search />
				</a-form-item>

				<a-form-item class="!mb-5" label="Address" name="address" required>
					<a-input v-model:value="formData.address" class="custom-input !bg-gray-50 !px-5" />
				</a-form-item>

				<a-button
					class="default-btn mx-auto mt-12 w-full justify-center bg-primary-2 text-white"
					html-type="submit"
					:disabled="isFormSame || loading"
					:loading="loading"
				>
					Update Profile
				</a-button>
			</a-form>
		</div>
	</div>
</template>
